import { authenticatedFetch } from "./authenticatedFetch";
import { formatApiErrorMessage } from "../utils/apiErrorMessage";
import {
  fetchSignatureFields,
  saveSignatureFields,
} from "./signatureFieldsService";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/** POST multipart PDF bytes; pairs with DOCX at `…/upload/docx`. Override via `VITE_MANUAL_DOCUMENT_UPLOAD_PATH`. */
export const MANUAL_DOCUMENT_UPLOAD_PATH =
  import.meta.env.VITE_MANUAL_DOCUMENT_UPLOAD_PATH ||
  "/documents/manual/upload/pdf";

/**
 * POST multipart DOCX URL: `…/upload/docx`, derived from {@link MANUAL_DOCUMENT_UPLOAD_PATH}
 * when it ends with `pdf`, else `{base}/docx`.
 */
export function resolveManualDocumentUploadDocxPath() {
  const fromEnv = import.meta.env.VITE_MANUAL_DOCUMENT_UPLOAD_DOCX_PATH;
  if (fromEnv && typeof fromEnv === "string") return fromEnv;

  const base = MANUAL_DOCUMENT_UPLOAD_PATH;
  if (/pdf$/i.test(base)) {
    return base.replace(/pdf$/i, "docx");
  }
  return `${String(base).replace(/\/+$/, "")}/docx`;
}

/** GET — load manual draft by job (query `job_id`). */
export const MANUAL_DOCUMENT_GET_PATH =
  import.meta.env.VITE_MANUAL_DOCUMENT_GET_PATH || "/documents/manual";

const MANUAL_DOCUMENT_SAVE_PATH =
  import.meta.env.VITE_MANUAL_DOCUMENT_SAVE_PATH || "/documents/manual/save";

const MANUAL_DOCUMENT_GENERATE_BASE_PATH =
  import.meta.env.VITE_MANUAL_DOCUMENT_GENERATE_BASE_PATH || "/documents/manual";

const MANUAL_DOCUMENT_SIGNERS_BASE_PATH =
  import.meta.env.VITE_MANUAL_DOCUMENT_SIGNERS_BASE_PATH || "/documents/manual";

function toHtmlFromText(text) {
  const safe = String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const blocks = safe
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((b) => `<p>${b.replace(/\n/g, "<br/>")}</p>`);
  return blocks.join("") || "<p></p>";
}

/**
 * Normalize Lambda/API JSON into something TipTap can consume.
 *
 * Preferred (in order):
 * 1. `document` — ProseMirror/TipTap JSON `{ type: "doc", content: [...] }`
 * 2. `html` — HTML fragment safe for TipTap StarterKit + common marks
 * 3. `text` | `content` — plain text (converted to paragraphs)
 */
function normalizeUploadResponse(data) {
  if (!data || typeof data !== "object") {
    return { ok: false, message: "Resposta inválida do servidor." };
  }

  const doc = data.document ?? data.tiptap_document ?? data.editor_document;
  if (doc && typeof doc === "object" && doc.type === "doc" && Array.isArray(doc.content)) {
    return { ok: true, kind: "tiptap_json", document: doc };
  }

  const htmlRaw =
    typeof data.html === "string" && data.html.trim()
      ? data.html.trim()
      : typeof data.text === "string" && data.text.trim()
        ? toHtmlFromText(data.text)
        : typeof data.content === "string" && data.content.trim() && !data.content.trim().startsWith("{")
          ? toHtmlFromText(data.content)
          : "";

  if (htmlRaw) {
    return { ok: true, kind: "html", html: htmlRaw };
  }

  return {
    ok: false,
    message: data.message || data.error || "O servidor não retornou html nem document JSON.",
  };
}

/**
 * POST — duplicate manual document by job_id (empty body). Success only when status is 200.
 *
 * @param {string} jobId
 * @returns {Promise<{ ok: true } | { ok: false; message: string }>}
 */
export async function duplicateDocument(jobId) {
  if (!jobId || typeof jobId !== "string") {
    return { ok: false, message: "Identificador do documento inválido." };
  }

  try {
    const url = `${API_BASE_URL}${MANUAL_DOCUMENT_GENERATE_BASE_PATH}/${encodeURIComponent(String(jobId))}/duplicate`;
    const response = await authenticatedFetch(url, { method: "POST" });

    if (response.status === 200) {
      return { ok: true };
    }

    let body = null;
    try {
      body = await response.json();
    } catch {
      /* ignore */
    }
    const message = formatApiErrorMessage(
      body,
      `Não foi possível duplicar o documento (${response.status}).`
    );
    return { ok: false, message };
  } catch (error) {
    console.error("duplicateDocument:", error);
    return {
      ok: false,
      message: formatApiErrorMessage(error, "Erro de rede ao duplicar o documento."),
    };
  }
}

/**
 * Envia o PDF (bytes do arquivo) para o Lambda/backend e devolve conteúdo para o editor TipTap.
 *
 * @param {File} file — arquivo PDF escolhido pelo usuário (`file` contém os bytes)
 * @returns {Promise<
 *   | { ok: true; kind: 'html'; html: string }
 *   | { ok: true; kind: 'tiptap_json'; document: object }
 *   | { ok: false; message: string }
 * >}
 */
export async function uploadManualDocumentPdf(file) {
  if (!file || !(file instanceof Blob)) {
    return { ok: false, message: "Arquivo PDF inválido." };
  }

  try {
    const endpoint = `${API_BASE_URL}${MANUAL_DOCUMENT_UPLOAD_PATH}`;
    const form = new FormData();
    form.append("file", file, file.name || "document.pdf");

    const response = await authenticatedFetch(endpoint, {
      method: "POST",
      body: form,
    });

    if (!response.ok) {
      let message = "Não foi possível processar o PDF.";
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        /* ignore */
      }
      return { ok: false, message };
    }

    const data = await response.json();
    return normalizeUploadResponse(data);
  } catch (error) {
    console.error("uploadManualDocumentPdf:", error);
    return {
      ok: false,
      message: "Erro de rede ao enviar o PDF.",
    };
  }
}

/**
 * Same contract as {@link uploadManualDocumentPdf}; POST multipart to the DOCX upload route.
 *
 * @param {File} file
 */
export async function uploadManualDocumentDocx(file) {
  if (!file || !(file instanceof Blob)) {
    return { ok: false, message: "Arquivo DOCX inválido." };
  }

  const docxPath = resolveManualDocumentUploadDocxPath();

  try {
    const endpoint = `${API_BASE_URL}${docxPath}`;
    const form = new FormData();
    form.append("file", file, file.name || "document.docx");

    const response = await authenticatedFetch(endpoint, {
      method: "POST",
      body: form,
    });

    if (!response.ok) {
      let message = "Não foi possível processar o DOCX.";
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        /* ignore */
      }
      return { ok: false, message };
    }

    const data = await response.json();
    return normalizeUploadResponse(data);
  } catch (error) {
    console.error("uploadManualDocumentDocx:", error);
    return {
      ok: false,
      message: "Erro de rede ao enviar o DOCX.",
    };
  }
}

/**
 * GET manual document for editing. Expects JSON with at least `editor_text` (TipTap JSON string or object).
 *
 * @param {string} jobId
 * @returns {Promise<{ ok: true; data: object } | { ok: false; message: string }>}
 */
export async function fetchManualDocument(jobId) {
  if (!jobId || typeof jobId !== "string") {
    return { ok: false, message: "job_id inválido." };
  }

  try {
    const params = new URLSearchParams();
    params.set("job_id", jobId);
    const url = `${API_BASE_URL}${MANUAL_DOCUMENT_GET_PATH}?${params.toString()}`;

    const response = await authenticatedFetch(url, { method: "GET" });

    if (!response.ok) {
      let message = "Não foi possível carregar o documento.";
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        /* ignore */
      }
      return { ok: false, message };
    }

    const data = await response.json();
    return { ok: true, data };
  } catch (error) {
    console.error("fetchManualDocument:", error);
    return { ok: false, message: "Erro de rede ao carregar o documento." };
  }
}

/**
 * @param {object} payload — { job_id, body, signatures, is_manual }
 */
export async function saveManualDocument(payload) {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}${MANUAL_DOCUMENT_SAVE_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let message = "Não foi possível salvar o documento.";
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        /* ignore */
      }
      return { ok: false, message };
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await response.json();
      return { ok: true, data };
    }

    return { ok: true, data: null };
  } catch (error) {
    console.error("saveManualDocument:", error);
    return { ok: false, message: "Erro de rede ao salvar o documento." };
  }
}

/**
 * Generate contract/PDF from manual editor payload. Handles PDF blob, download URL, or base64 in JSON.
 *
 * @param {object} payload — same shape as save
 */
export async function generateManualDocument(payload) {
  const jobId = payload?.job_id;
  if (!jobId) {
    return { ok: false, message: "job_id obrigatório para gerar documento." };
  }

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${MANUAL_DOCUMENT_GENERATE_BASE_PATH}/${encodeURIComponent(String(jobId))}/generate`,
      {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      let message = "Não foi possível gerar o contrato.";
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        /* ignore */
      }
      return { ok: false, message };
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/pdf")) {
      const blob = await response.blob();
      return { ok: true, type: "pdf_blob", blob };
    }

    if (!contentType.includes("application/json")) {
      // 200 with empty/non-JSON body means generation accepted/success.
      return { ok: true, type: "success_only" };
    }

    let data = null;
    try {
      data = await response.json();
    } catch {
      return { ok: true, type: "success_only" };
    }

    if (data?.download_url) {
      return { ok: true, type: "download_url", downloadUrl: data.download_url };
    }
    if (data?.pdf_base64) {
      const bytes = Uint8Array.from(atob(data.pdf_base64), (c) => c.charCodeAt(0));
      return { ok: true, type: "pdf_blob", blob: new Blob([bytes], { type: "application/pdf" }) };
    }

    return { ok: true, type: "success_only" };
  } catch (error) {
    console.error("generateManualDocument:", error);
    return { ok: false, message: "Erro de rede ao gerar o contrato." };
  }
}

/** @deprecated Use fetchSignatureFields from signatureFieldsService.js */
export async function fetchManualDocumentSigners(jobId) {
  return fetchSignatureFields(jobId);
}

/** @deprecated Use saveSignatureFields from signatureFieldsService.js */
export async function saveManualDocumentSigners(jobId, signersOrPayload) {
  return saveSignatureFields(jobId, signersOrPayload);
}
